import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { fetchAssetPreviewImage } from '../lib/api/assets'
import { selectors } from '../reducers/appReducer';
import noPhotoImg from '../images/no-photo.png';
import { getAssetURL } from '../api';

export const useNewAssetPreview = (assets) => {
    const [previewImages, setPreviewImages] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const imagePlaceholder = useSelector(selectors.getDefaultPlaceholderImage)?.imageUrl
    const imagePlaceholderUrl = imagePlaceholder === './images/no-photo.png' || imagePlaceholder === undefined ? noPhotoImg : imagePlaceholder

    const previewFileExtensionList = useSelector(selectors.getPreviewFileExtensionList) || []

    // Normalizza l'input: converte singolo asset in array
    const normalizedAssets = useMemo(() => {
        if (!assets) return [];
        return Array.isArray(assets) ? assets : [assets];
    }, [assets]);

    // Crea una chiave stabile per gli assets per evitare loop infiniti
    // Ora usa 'url' invece di 'src' e 'id' per maggiore stabilità
    const assetsKey = useMemo(() => {
        if (normalizedAssets.length === 0) return '';
        return normalizedAssets.map(a => a.id || a.url).join('|');
    }, [normalizedAssets])

    const checkFileExtension = (filename, extensionsList) => {
        const lowerName = filename.toLowerCase();
        return extensionsList.some(extension => lowerName.endsWith(extension));
    }

    // Controlla se è un'immagine diretta usando l'extension field
    const isDirectImageFile = (asset) => {
        const extension = asset.extension?.toLowerCase();
        return extension === 'jpg' ||
            extension === 'jpeg' ||
            extension === 'png' ||
            extension === 'gif' ||
            extension === 'webp';
    }

    // Alternativa: controlla dall'URL se extension non è disponibile
    const isDirectImageFromUrl = (url) => {
        const lowerUrl = url.toLowerCase();
        return lowerUrl.endsWith('.jpg') ||
            lowerUrl.endsWith('.jpeg') ||
            lowerUrl.endsWith('.png') ||
            lowerUrl.endsWith('.gif') ||
            lowerUrl.endsWith('.webp');
    }

    useEffect(() => {
        const fetchAllPreviews = async () => {
            try {
                setLoading(true);
                const previews = {};

                for (const asset of normalizedAssets) {
                    // Per asset articolo con public_url, asset.url è relativo, costruisco URL completo
                    // Per asset correlati, asset.url è già l'URL completo
                    const assetUrl = asset.public_url ? getAssetURL(asset.url) : asset.url;
                    const assetId = asset.id || asset.url; // Uso id come chiave primaria

                    // Se ha già un public_url E è un'immagine, uso quello direttamente
                    // Per altri file (PDF, etc.) devo comunque fare fetch della preview
                    if (asset.public_url && (isDirectImageFile(asset) || isDirectImageFromUrl(asset.public_url))) {
                        previews[assetId] = asset.public_url;
                        continue;
                    }

                    // Controllo prima l'extension field, poi l'URL
                    const isImage = isDirectImageFile(asset) || isDirectImageFromUrl(assetUrl);

                    if (!checkFileExtension(assetUrl, previewFileExtensionList)) {
                        // Se è un'immagine diretta, uso l'URL originale
                        if (isImage) {
                            previews[assetId] = assetUrl;
                        } else {
                            previews[assetId] = imagePlaceholderUrl;
                        }
                        continue;
                    }

                    // Fetch preview per altri tipi di file supportati (PDF, etc.)
                    const params = { name: assetUrl }
                    const result = await fetchAssetPreviewImage(params)

                    if (!result) {
                        previews[assetId] = imagePlaceholderUrl
                    } else {
                        previews[assetId] = result
                    }
                }

                setPreviewImages(previews);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        if (normalizedAssets.length > 0) {
            fetchAllPreviews()
        } else {
            // Se non ci sono assets, resetto le preview images
            setPreviewImages({});
            setLoading(false);
        }
    }, [assetsKey, imagePlaceholderUrl, previewFileExtensionList]);

    // Ritorno anche una funzione helper per ottenere la preview di un singolo asset
    const getPreviewUrl = (assetIdOrUrl) => {
        return previewImages[assetIdOrUrl] || imagePlaceholderUrl;
    };

    // Se l'input era un singolo asset, ritorna direttamente la sua preview
    const singlePreview = !Array.isArray(assets) && normalizedAssets.length === 1
        ? previewImages[normalizedAssets[0].id || normalizedAssets[0].url]
        : null;

    return {
        previewImages,      // Oggetto con tutte le preview (chiave: asset.id)
        singlePreview,      // Preview diretta se input era singolo asset
        getPreviewUrl,      // Helper function per ottenere una preview specifica
        loading,
        error
    };
};